import config from '../config/environment';
import supabase from '../config/supabase';
import type {
  StudentHistoryImportRow,
  StudentHistoryMatchResult,
} from '../utils/studentWorkoutHistoryImport';
import { handleServiceError } from '../utils/errorHandler';

const IMPORT_FUNCTION_NAME = 'import-student-workout-history';
const COMMIT_BATCH_SIZE = 200;
const ALREADY_IMPORTED_MESSAGE = 'Esses históricos já estão cadastrados.';

export interface StudentHistoryImportCommitResult {
  importedCount: number;
}

interface CommitResponse {
  importedCount?: number;
  error?: string;
}

interface CommitRow {
  studentId: number;
  code: string;
  currentTrainingCode: string | null;
  periodYear: number;
  periodMonth: number;
  workoutDay: number | null;
  expectedDeliveryDay: number | null;
}

class StudentWorkoutHistoryImportServiceClass {
  private async getFunctionHeaders(): Promise<Record<string, string>> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      apikey: config.supabaseAnonKey,
    };
  }

  private async post<TResponse>(body: unknown, context: string): Promise<TResponse> {
    const headers = await this.getFunctionHeaders();
    const response = await fetch(`${config.supabaseFunctionsUrl}/${IMPORT_FUNCTION_NAME}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    const payload = await response.json().catch(() => ({})) as TResponse & { error?: string };

    if (!response.ok) {
      handleServiceError(new Error(payload?.error ?? `Erro ao ${context}`), context);
    }

    return payload;
  }

  async match(rows: readonly StudentHistoryImportRow[]): Promise<StudentHistoryMatchResult> {
    const studentNames = Array.from(new Set(rows.map((row) => row.studentName)));
    const trainingCodes = Array.from(new Set(rows.filter((row) => row.code.length > 0).map((row) => row.code)));
    if (studentNames.length === 0) {
      return { studentsByName: {}, trainingsByCode: {}, existingCompetences: [] };
    }

    return this.post<StudentHistoryMatchResult>({
      action: 'match',
      studentNames,
      trainingCodes,
    }, 'conferir alunas e treinos');
  }

  async commit(rows: readonly StudentHistoryImportRow[]): Promise<StudentHistoryImportCommitResult> {
    const pending = rows.flatMap((row): CommitRow[] => {
      if (row.studentId === null) {
        return [];
      }
      return [{
        studentId: row.studentId,
        code: row.code,
        currentTrainingCode: row.currentTrainingCode,
        periodYear: row.periodYear,
        periodMonth: row.periodMonth,
        workoutDay: row.workoutDay,
        expectedDeliveryDay: row.expectedDeliveryDay,
      }];
    });

    let importedCount = 0;

    for (let offset = 0; offset < pending.length; offset += COMMIT_BATCH_SIZE) {
      const batch = pending.slice(offset, offset + COMMIT_BATCH_SIZE);

      try {
        const payload = await this.post<CommitResponse>({ action: 'commit', rows: batch }, 'importar histórico de treinos');
        importedCount += payload.importedCount ?? 0;
      } catch (caught) {
        const message = caught instanceof Error ? caught.message : '';
        if (message === ALREADY_IMPORTED_MESSAGE) {
          continue;
        }
        if (importedCount > 0) {
          throw new Error(`A importação parou depois de ${importedCount} históricos. ${message}`.trim());
        }
        throw caught;
      }
    }

    if (importedCount === 0) {
      throw new Error(ALREADY_IMPORTED_MESSAGE);
    }

    return { importedCount };
  }
}

export const StudentWorkoutHistoryImportService = new StudentWorkoutHistoryImportServiceClass();
